import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import '../styles/Teams.css';
import { EQUIPMENT_CATALOG, MATERIAL_CATALOG, UNIT_OPTIONS } from '../constants/resourceCatalog';

const MANUAL_OPTION = '__manual__';
const API_BASE = API_BASE_URL;
const STATUS_OPTIONS = [
  { value: 'available', label: 'Müsait' },
  { value: 'reserved', label: 'Rezerve' },
  { value: 'in_use', label: 'Kullanımda' },
  { value: 'maintenance', label: 'Bakımda' },
  { value: 'out_of_service', label: 'Kullanım Dışı' },
];
const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
const PURCHASE_STATUS_OPTIONS = [
  { value: 'draft', label: 'Taslak' },
  { value: 'requested', label: 'Talep Açıldı' },
  { value: 'ordered', label: 'Sipariş Geçildi' },
  { value: 'partially_received', label: 'Kısmi Teslim' },
  { value: 'received', label: 'Teslim Alındı' },
  { value: 'cancelled', label: 'İptal' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending_invoice', label: 'Fatura Bekleniyor' },
  { value: 'invoice_received', label: 'Fatura Geldi' },
  { value: 'approved', label: 'Onaylandı' },
  { value: 'paid', label: 'Ödendi' },
];

const PURCHASE_DOCUMENT_TYPE_OPTIONS = [
  { value: 'invoice', label: 'Fatura' },
  { value: 'delivery_note', label: 'İrsaliye' },
  { value: 'quote', label: 'Teklif' },
  { value: 'payment_receipt', label: 'Ödeme Belgesi' },
  { value: 'other', label: 'Diğer' },
];

const emptyPurchaseOrderState = {
  resource_id: '',
  project_id: '',
  supplier_name: '',
  quantity: '',
  unit: '',
  unit_price: '',
  total_amount: '',
  status: 'draft',
  requested_date: new Date().toISOString().slice(0, 10),
  expected_delivery_date: '',
  delivered_date: '',
  received_quantity: '',
  invoice_number: '',
  invoice_date: '',
  invoice_amount: '',
  payment_status: 'pending_invoice',
  notes: '',
};

const emptyPurchaseDocumentState = {
  document_type: 'invoice',
  file: null,
};

const emptyUsageLogState = {
  resource_id: '',
  project_id: '',
  usage_date: new Date().toISOString().slice(0, 10),
  quantity: '',
  unit: '',
  notes: '',
};

const emptyFormState = {
  type: 'equipment',
  catalog_name: '',
  custom_name: '',
  unit: 'Adet',
  custom_unit: '',
  status: 'available',
  stock_quantity: '',
  reorder_point: '',
  lead_time_days: '',
  avg_daily_usage: '',
  supplier_name: '',
  notes: '',
};

const formatNumber = (value, digits = 0) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return numeric.toLocaleString('tr-TR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const parseNumericField = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const numeric = Number(String(value).replace(',', '.'));
  return Number.isFinite(numeric) ? numeric : 0;
};

const Resources = () => {
  const { user, permissions } = useAuth();
  const [resources, setResources] = useState([]);
  const [forecast, setForecast] = useState({ summary: null, rows: [] });
  const [projects, setProjects] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [selectedOrderForDocs, setSelectedOrderForDocs] = useState(null);
  const [orderDocuments, setOrderDocuments] = useState([]);
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRes, setEditingRes] = useState(null);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [editingPurchaseOrder, setEditingPurchaseOrder] = useState(null);
  const [showUsageForm, setShowUsageForm] = useState(false);
  const [editingUsageLog, setEditingUsageLog] = useState(null);
  const [newRes, setNewRes] = useState(emptyFormState);
  const [purchaseOrderForm, setPurchaseOrderForm] = useState(emptyPurchaseOrderState);
  const [usageLogForm, setUsageLogForm] = useState(emptyUsageLogState);
  const [purchaseDocumentForm, setPurchaseDocumentForm] = useState(emptyPurchaseDocumentState);

  const canManageResources = hasEffectivePermission(user, permissions, 'can_manage_resources');
  const canManageDocuments = hasEffectivePermission(user, permissions, 'can_manage_documents');

  const selectedResource = useMemo(
    () => resources.find((item) => Number(item.id) === Number(selectedResourceId)) || null,
    [resources, selectedResourceId]
  );

  const selectedForecast = useMemo(
    () => forecast.rows.find((item) => Number(item.id) === Number(selectedResourceId)) || null,
    [forecast.rows, selectedResourceId]
  );

  const criticalForecastRows = useMemo(
    () => forecast.rows.filter((row) => row.forecast_status === 'critical').slice(0, 5),
    [forecast.rows]
  );

  const warningForecastRows = useMemo(
    () => forecast.rows.filter((row) => row.forecast_status === 'warning').slice(0, 5),
    [forecast.rows]
  );

  const selectedResourceOrders = useMemo(
    () => purchaseOrders.filter((row) => Number(row.resource_id) === Number(selectedResourceId)),
    [purchaseOrders, selectedResourceId]
  );

  const selectedResourceUsageLogs = useMemo(
    () => usageLogs.filter((row) => Number(row.resource_id) === Number(selectedResourceId)),
    [usageLogs, selectedResourceId]
  );

  const fetchResources = useCallback(async () => {
    try {
      const [resourceRes, forecastRes, projectRes, purchaseOrderRes, usageLogRes] = await Promise.all([
        apiClient.get(`${API_BASE}/resources`),
        apiClient.get(`${API_BASE}/resources/supply-forecast`),
        apiClient.get(`${API_BASE}/projects`),
        apiClient.get(`${API_BASE}/purchase-orders`),
        apiClient.get(`${API_BASE}/material-usage`),
      ]);
      setResources(Array.isArray(resourceRes.data) ? resourceRes.data : []);
      setForecast({
        summary: forecastRes.data?.summary || null,
        rows: Array.isArray(forecastRes.data?.rows) ? forecastRes.data.rows : [],
      });
      setProjects(Array.isArray(projectRes.data) ? projectRes.data : []);
      setPurchaseOrders(Array.isArray(purchaseOrderRes.data) ? purchaseOrderRes.data : []);
      setUsageLogs(Array.isArray(usageLogRes.data) ? usageLogRes.data : []);
    } catch (err) {
      setResources([]);
      setForecast({ summary: null, rows: [] });
      setProjects([]);
      setPurchaseOrders([]);
      setUsageLogs([]);
    }
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  useEffect(() => {
    if (resources.length === 0) {
      setSelectedResourceId(null);
      return;
    }
    const exists = resources.some((item) => Number(item.id) === Number(selectedResourceId));
    if (!exists) {
      setSelectedResourceId(resources[0].id);
    }
  }, [resources, selectedResourceId]);

  const buildPayload = (formState) => {
    const manualName = formState.catalog_name === MANUAL_OPTION;
    const manualUnit = formState.unit === MANUAL_OPTION;

    return {
      name: manualName ? formState.custom_name : formState.catalog_name,
      type: formState.type,
      unit: manualUnit ? formState.custom_unit : formState.unit,
      status: formState.status || 'available',
      stock_quantity: parseNumericField(formState.stock_quantity),
      reorder_point: parseNumericField(formState.reorder_point),
      lead_time_days: parseNumericField(formState.lead_time_days),
      avg_daily_usage: parseNumericField(formState.avg_daily_usage),
      supplier_name: formState.supplier_name || '',
      notes: formState.notes || '',
    };
  };

  const handleCreate = async (e) => {
    if (!canManageResources) return;
    e.preventDefault();
    const payload = buildPayload(newRes);
    if (!payload.name) {
      window.alert('Kaynak adı seçiniz');
      return;
    }

    try {
      await apiClient.post(`${API_BASE}/resources`, payload);
      setShowForm(false);
      setNewRes(emptyFormState);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Kaynak oluşturulamadı');
    }
  };

  const handleDelete = async (resourceId) => {
    if (!canManageResources) return;
    if (!window.confirm('Bu kaynağı silmek istiyor musunuz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/resources/${resourceId}`);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Kaynak silinemedi');
    }
  };

  const openEditModal = (resource) => {
    if (!canManageResources) return;
    const isEquipment = resource.type === 'equipment';
    const catalog = isEquipment ? EQUIPMENT_CATALOG : MATERIAL_CATALOG;
    const knownName = catalog.includes(resource.name);
    const knownUnit = UNIT_OPTIONS.includes(resource.unit || '');
    setEditingRes({
      id: resource.id,
      type: resource.type,
      catalog_name: knownName ? resource.name : MANUAL_OPTION,
      custom_name: knownName ? '' : resource.name || '',
      unit: knownUnit ? (resource.unit || '') : MANUAL_OPTION,
      custom_unit: knownUnit ? '' : (resource.unit || ''),
      status: resource.status || 'available',
      stock_quantity: resource.stock_quantity ?? '',
      reorder_point: resource.reorder_point ?? '',
      lead_time_days: resource.lead_time_days ?? '',
      avg_daily_usage: resource.avg_daily_usage ?? '',
      supplier_name: resource.supplier_name || '',
      notes: resource.notes || '',
    });
  };

  const openCreatePurchaseOrder = (resource, forecastRow = null) => {
    if (!canManageResources) return;
    setPurchaseOrderForm({
      ...emptyPurchaseOrderState,
      resource_id: resource.id,
      supplier_name: resource.supplier_name || '',
      quantity: forecastRow?.recommended_order_quantity || '',
      unit: resource.unit || '',
      notes: forecastRow?.recommendation || '',
    });
    setEditingPurchaseOrder(null);
    setShowPurchaseForm(true);
  };

  const openEditPurchaseOrder = (row) => {
    if (!canManageResources) return;
    setEditingPurchaseOrder(row);
    setPurchaseOrderForm({
      resource_id: row.resource_id || '',
      project_id: row.project_id || '',
      supplier_name: row.supplier_name || '',
      quantity: row.quantity ?? '',
      unit: row.unit || '',
      unit_price: row.unit_price ?? '',
      total_amount: row.total_amount ?? '',
      status: row.status || 'draft',
      requested_date: row.requested_date ? String(row.requested_date).slice(0, 10) : emptyPurchaseOrderState.requested_date,
      expected_delivery_date: row.expected_delivery_date ? String(row.expected_delivery_date).slice(0, 10) : '',
      delivered_date: row.delivered_date ? String(row.delivered_date).slice(0, 10) : '',
      received_quantity: row.received_quantity ?? '',
      invoice_number: row.invoice_number || '',
      invoice_date: row.invoice_date ? String(row.invoice_date).slice(0, 10) : '',
      invoice_amount: row.invoice_amount ?? '',
      payment_status: row.payment_status || 'pending_invoice',
      notes: row.notes || '',
    });
    setShowPurchaseForm(true);
  };

  const openCreateUsageLog = (resource) => {
    if (!canManageResources) return;
    setUsageLogForm({
      ...emptyUsageLogState,
      resource_id: resource.id,
      unit: resource.unit || '',
    });
    setEditingUsageLog(null);
    setShowUsageForm(true);
  };

  const openEditUsageLog = (row) => {
    if (!canManageResources) return;
    setEditingUsageLog(row);
    setUsageLogForm({
      resource_id: row.resource_id || '',
      project_id: row.project_id || '',
      usage_date: row.usage_date ? String(row.usage_date).slice(0, 10) : emptyUsageLogState.usage_date,
      quantity: row.quantity ?? '',
      unit: row.unit || '',
      notes: row.notes || '',
    });
    setShowUsageForm(true);
  };

  const handleUpdate = async (e) => {
    if (!canManageResources) return;
    e.preventDefault();
    if (!editingRes?.id) return;
    const payload = buildPayload(editingRes);
    if (!payload.name) {
      window.alert('Kaynak adı seçiniz');
      return;
    }

    try {
      await apiClient.put(`${API_BASE}/resources/${editingRes.id}`, payload);
      setEditingRes(null);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Kaynak güncellenemedi');
    }
  };

  const renderForecastPill = (row) => {
    if (!row) return null;
    const className = row.forecast_status === 'critical'
      ? 'danger'
      : row.forecast_status === 'warning'
        ? 'warning'
        : '';
    return (
      <span className={`team-badge ${className}`}>
        {row.forecast_status === 'critical' ? 'Kritik stok' : row.forecast_status === 'warning' ? 'Sipariş planı' : 'Dengeli'}
      </span>
    );
  };

  const handlePurchaseFieldChange = (key, value) => {
    setPurchaseOrderForm((prev) => {
      const next = { ...prev, [key]: value };
      const quantity = parseNumericField(next.quantity);
      const unitPrice = parseNumericField(next.unit_price);
      if (key === 'quantity' || key === 'unit_price') {
        next.total_amount = quantity > 0 && unitPrice > 0 ? String((quantity * unitPrice).toFixed(2)) : next.total_amount;
      }
      return next;
    });
  };

  const handlePurchaseSubmit = async (e) => {
    if (!canManageResources) return;
    e.preventDefault();
    const payload = {
      ...purchaseOrderForm,
      resource_id: Number(purchaseOrderForm.resource_id) || null,
      project_id: Number(purchaseOrderForm.project_id) || null,
      quantity: parseNumericField(purchaseOrderForm.quantity),
      unit_price: parseNumericField(purchaseOrderForm.unit_price),
      total_amount: parseNumericField(purchaseOrderForm.total_amount),
      received_quantity: parseNumericField(purchaseOrderForm.received_quantity),
      invoice_amount: parseNumericField(purchaseOrderForm.invoice_amount),
    };

    if (!payload.resource_id || payload.quantity <= 0) {
      window.alert('Kaynak ve miktar zorunludur.');
      return;
    }

    try {
      if (editingPurchaseOrder?.id) {
        await apiClient.put(`${API_BASE}/purchase-orders/${editingPurchaseOrder.id}`, payload);
      } else {
        await apiClient.post(`${API_BASE}/purchase-orders`, payload);
      }
      setShowPurchaseForm(false);
      setEditingPurchaseOrder(null);
      setPurchaseOrderForm(emptyPurchaseOrderState);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Sipariş kaydedilemedi');
    }
  };


  const fetchPurchaseOrderDocuments = useCallback(async (orderId) => {
    try {
      const response = await apiClient.get(`${API_BASE}/purchase-orders/${orderId}/documents`);
      setOrderDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setOrderDocuments([]);
      window.alert(err.response?.data?.message || 'Sipariş belgeleri yüklenemedi');
    }
  }, []);

  const openPurchaseDocumentModal = async (order) => {
    if (!canManageDocuments) return;
    setSelectedOrderForDocs(order);
    setPurchaseDocumentForm(emptyPurchaseDocumentState);
    await fetchPurchaseOrderDocuments(order.id);
  };

  const handleUploadPurchaseDocument = async (e) => {
    if (!canManageDocuments) return;
    e.preventDefault();
    if (!selectedOrderForDocs?.id || !purchaseDocumentForm.file) {
      window.alert('Belge seçmeniz gerekiyor.');
      return;
    }

    try {
      const form = new FormData();
      form.append('document', purchaseDocumentForm.file);
      form.append('document_type', purchaseDocumentForm.document_type || 'invoice');
      await apiClient.post(`${API_BASE}/purchase-orders/${selectedOrderForDocs.id}/documents`, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setPurchaseDocumentForm(emptyPurchaseDocumentState);
      await fetchPurchaseOrderDocuments(selectedOrderForDocs.id);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Belge yüklenemedi');
    }
  };

  const handleDownloadPurchaseDocument = async (doc) => {
    try {
      const response = await apiClient.get(`${API_BASE}/purchase-orders/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name || doc.file_path?.split('/').pop() || `purchase-doc-${doc.id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Belge indirilemedi');
    }
  };

  const handleDeletePurchaseDocument = async (docId) => {
    if (!canManageDocuments) return;
    if (!window.confirm('Bu belgeyi silmek istiyor musunuz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/purchase-orders/documents/${docId}`);
      if (selectedOrderForDocs?.id) {
        await fetchPurchaseOrderDocuments(selectedOrderForDocs.id);
      }
    } catch (err) {
      window.alert(err.response?.data?.message || 'Belge silinemedi');
    }
  };

  const handleDeletePurchaseOrder = async (id) => {
    if (!canManageResources) return;
    if (!window.confirm('Bu sipariş kaydını silmek istiyor musunuz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/purchase-orders/${id}`);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Sipariş silinemedi');
    }
  };

  const handleUsageFieldChange = (key, value) => {
    setUsageLogForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleUsageSubmit = async (e) => {
    if (!canManageResources) return;
    e.preventDefault();
    const payload = {
      ...usageLogForm,
      resource_id: Number(usageLogForm.resource_id) || null,
      project_id: Number(usageLogForm.project_id) || null,
      quantity: parseNumericField(usageLogForm.quantity),
    };

    if (!payload.resource_id || payload.quantity <= 0) {
      window.alert('Kaynak ve tüketim miktarı zorunludur.');
      return;
    }

    try {
      if (editingUsageLog?.id) {
        await apiClient.put(`${API_BASE}/material-usage/${editingUsageLog.id}`, payload);
      } else {
        await apiClient.post(`${API_BASE}/material-usage`, payload);
      }
      setShowUsageForm(false);
      setEditingUsageLog(null);
      setUsageLogForm(emptyUsageLogState);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Kullanım kaydı kaydedilemedi');
    }
  };

  const handleDeleteUsageLog = async (id) => {
    if (!canManageResources) return;
    if (!window.confirm('Bu tüketim kaydını silmek istiyor musunuz?')) return;
    try {
      await apiClient.delete(`${API_BASE}/material-usage/${id}`);
      await fetchResources();
    } catch (err) {
      window.alert(err.response?.data?.message || 'Kullanım kaydı silinemedi');
    }
  };

  const renderForm = (formState, setFormState, onSubmit, title, onClose) => {
    const nameOptions = formState.type === 'equipment' ? EQUIPMENT_CATALOG : MATERIAL_CATALOG;
    const manualName = formState.catalog_name === MANUAL_OPTION;
    const manualUnit = formState.unit === MANUAL_OPTION;
    const isMaterial = formState.type === 'material';

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h3>{title}</h3>
          <form onSubmit={onSubmit}>
            <div className="form-group">
              <label>Tip</label>
              <select
                className="form-control"
                value={formState.type}
                onChange={(e) => setFormState((prev) => ({ ...prev, type: e.target.value, catalog_name: '', custom_name: '' }))}
              >
                <option value="equipment">Ekipman</option>
                <option value="material">Malzeme</option>
              </select>
            </div>

            <div className="form-group">
              <label>Kaynak Adı</label>
              <select
                className="form-control"
                value={formState.catalog_name}
                onChange={(e) => setFormState((prev) => ({ ...prev, catalog_name: e.target.value, custom_name: '' }))}
                required
              >
                <option value="">Listeden seçin</option>
                {nameOptions.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
                <option value={MANUAL_OPTION}>Listede yok (manuel gir)</option>
              </select>
            </div>

            {manualName && (
              <div className="form-group">
                <label>Manuel Kaynak Adı</label>
                <input
                  className="form-control"
                  type="text"
                  value={formState.custom_name}
                  onChange={(e) => setFormState((prev) => ({ ...prev, custom_name: e.target.value }))}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Birim</label>
              <select
                className="form-control"
                value={formState.unit}
                onChange={(e) => setFormState((prev) => ({ ...prev, unit: e.target.value, custom_unit: '' }))}
              >
                {UNIT_OPTIONS.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
                <option value={MANUAL_OPTION}>Birim manuel gir</option>
              </select>
            </div>

            {manualUnit && (
              <div className="form-group">
                <label>Manuel Birim</label>
                <input
                  className="form-control"
                  type="text"
                  value={formState.custom_unit}
                  onChange={(e) => setFormState((prev) => ({ ...prev, custom_unit: e.target.value }))}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Durum</label>
              <select
                className="form-control"
                value={formState.status}
                onChange={(e) => setFormState((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            {isMaterial && (
              <>
                <div className="resource-section-title">Stok ve Tedarik Planı</div>
                <div className="resource-form-grid">
                  <div className="form-group">
                    <label>Mevcut Stok</label>
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formState.stock_quantity}
                      onChange={(e) => setFormState((prev) => ({ ...prev, stock_quantity: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Sipariş Eşiği</label>
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formState.reorder_point}
                      onChange={(e) => setFormState((prev) => ({ ...prev, reorder_point: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tedarik Süresi (gün)</label>
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      step="1"
                      value={formState.lead_time_days}
                      onChange={(e) => setFormState((prev) => ({ ...prev, lead_time_days: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Elle Günlük Tüketim</label>
                    <input
                      className="form-control"
                      type="number"
                      min="0"
                      step="0.01"
                      value={formState.avg_daily_usage}
                      onChange={(e) => setFormState((prev) => ({ ...prev, avg_daily_usage: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Tedarikçi</label>
                  <input
                    className="form-control"
                    type="text"
                    value={formState.supplier_name}
                    onChange={(e) => setFormState((prev) => ({ ...prev, supplier_name: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Not</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formState.notes}
                    onChange={(e) => setFormState((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-submit">{title.includes('Düzenle') ? 'Güncelle' : 'Kaydet'}</button>
              <button type="button" className="btn-cancel" onClick={onClose}>İptal</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="teams-container resources-layout">
      <div className="teams-sidebar resources-list-pane">
        <div className="sidebar-header-teams">
          <h3>Kaynaklar</h3>
          {canManageResources && (
            <button onClick={() => setShowForm(true)} className="btn-add" type="button" aria-label="Kaynak ekle">
              <span className="btn-add-icon">+</span>
            </button>
          )}
        </div>

        <div className="teams-list">
          {resources.map((res) => {
            const forecastRow = forecast.rows.find((row) => Number(row.id) === Number(res.id));
            return (
              <div
                key={res.id}
                className={`team-item ${Number(selectedResourceId) === Number(res.id) ? 'active' : ''}`}
                onClick={() => setSelectedResourceId(res.id)}
              >
                <span className={`team-icon ${res.type === 'equipment' ? 'subcontractor' : 'internal'}`}>
                  {res.type === 'equipment' ? 'EK' : 'ML'}
                </span>
                <div className="resource-list-text">
                  <span className="team-item-name">{res.name}</span>
                  <span className="resource-list-meta">
                    {res.type === 'equipment' ? 'Ekipman' : 'Malzeme'} ? {STATUS_LABEL_MAP[res.status] || 'Müsait'}
                  </span>
                </div>
                {forecastRow && res.type === 'material' ? renderForecastPill(forecastRow) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="teams-content resources-detail-pane">
        {!selectedResource ? (
          <div className="empty-selection">
            <p>Soldan bir kaynak seçerek detaylarını görüntüleyin.</p>
          </div>
        ) : (
          <div className="resource-detail-card">
            <div className="content-header">
              <h2>
                {selectedResource.name}
                <span className="team-badge">
                  {selectedResource.type === 'equipment' ? 'Ekipman' : 'Malzeme'}
                </span>
                {selectedForecast ? renderForecastPill(selectedForecast) : null}
              </h2>
              {canManageResources && (
                <div className="emp-actions">
                  <button className="btn-icon" onClick={() => openEditModal(selectedResource)} type="button">Düzenle</button>
                  <button className="btn-icon danger" onClick={() => handleDelete(selectedResource.id)} type="button">Sil</button>
                </div>
              )}
            </div>

            <div className="resource-detail-grid">
              <div className="summary-item">
                <div className="summary-label">Kaynak Türü</div>
                <div className="summary-value">{selectedResource.type === 'equipment' ? 'Ekipman' : 'Malzeme'}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Birim</div>
                <div className="summary-value">{selectedResource.unit || '-'}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Durum</div>
                <div className="summary-value">{STATUS_LABEL_MAP[selectedResource.status] || 'Müsait'}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Kayıt Tarihi</div>
                <div className="summary-value">
                  {selectedResource.created_at ? new Date(selectedResource.created_at).toLocaleDateString('tr-TR') : '-'}
                </div>
              </div>
            </div>

            {selectedResource.type === 'material' && (
              <>
                <div className="resource-section-title">Stok Özeti</div>
                <div className="resource-detail-grid">
                  <div className="summary-item">
                    <div className="summary-label">Mevcut Stok</div>
                    <div className="summary-value">{formatNumber(selectedResource.stock_quantity, 2)} {selectedResource.unit || ''}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-label">Sipariş Eşiği</div>
                    <div className="summary-value">{formatNumber(selectedResource.reorder_point, 2)} {selectedResource.unit || ''}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-label">Tedarik Süresi</div>
                    <div className="summary-value">{formatNumber(selectedResource.lead_time_days, 0)} g?n</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-label">Tedarikçi</div>
                    <div className="summary-value">{selectedResource.supplier_name || '-'}</div>
                  </div>
                </div>

                {selectedForecast && (
                  <div className="resource-note-card">
                    <strong>Tedarik Tahmini</strong>
                    <div className="resource-forecast-metrics">
                      <div>
                        <span>Günlük tüketim</span>
                        <strong>{formatNumber(selectedForecast.daily_usage_rate, 2)} {selectedResource.unit || 'birim'}</strong>
                      </div>
                      <div>
                        <span>Stok tükenme</span>
                        <strong>{selectedForecast.days_until_stockout === null ? 'Veri yok' : `${formatNumber(selectedForecast.days_until_stockout, 1)} g?n`}</strong>
                      </div>
                      <div>
                        <span>Önerilen sipariş</span>
                        <strong>{formatNumber(selectedForecast.recommended_order_quantity, 2)} {selectedResource.unit || ''}</strong>
                      </div>
                    </div>
                    <p>{selectedForecast.recommendation}</p>
                    <p className="resource-note-muted">
                      {selectedForecast.usage_source === 'usage_logs'
                        ? `Son ${forecast.summary?.lookback_days || 30} günde ${formatNumber(selectedForecast.logged_usage_quantity, 2)} ${selectedResource.unit || 'birim'} gerçek tüketim işlendi.`
                        : `Günlük raporlarda son ${forecast.summary?.lookback_days || 30} günde ${selectedForecast.usage_mentions} kez geçti.`}
                      {selectedForecast.usage_source === 'manual_average'
                        ? ' Elle tanımlanan günlük tüketim değeri kullanıldı.'
                        : selectedForecast.usage_source === 'daily_report_mentions'
                          ? ' Günlük rapor verisi üzerinden hesaplandı.'
                          : ' Gerçek kullanım kayıtları baz alındı.'}
                    </p>
                    <div className="emp-actions">
                      {canManageResources && (
                        <button type="button" className="btn-primary" onClick={() => openCreatePurchaseOrder(selectedResource, selectedForecast)}>
                          Sipariş Talebi Oluştur
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="resource-note-card">
                <strong>Kullanım Notu</strong>
              <p>
                {selectedResource.type === 'equipment'
                  ? 'Bu ekipmanı görevler ve günlük raporlar ile ilişkilendirerek saha kullanım yoğunluğunu takip edebilirsiniz.'
                  : (selectedResource.notes || 'Malzemeyi stok seviyesi, sipariş eşiği ve günlük kullanım hızı ile birlikte takip edin.')}
              </p>
            </div>

            <div className="resource-note-card">
              <strong>Tedarik Merkezi</strong>
              <div className="resource-forecast-summary">
                <div className="summary-item">
                  <div className="summary-label">Kritik Malzeme</div>
                  <div className="summary-value">{forecast.summary?.critical_count ?? 0}</div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Planlanacak</div>
                  <div className="summary-value">{forecast.summary?.warning_count ?? 0}</div>
                </div>
                <div className="summary-item">
                  <div className="summary-label">Dengeli</div>
                  <div className="summary-value">{forecast.summary?.ok_count ?? 0}</div>
                </div>
              </div>
              <div className="resource-forecast-lists">
                <div>
                  <h4>Kritik Liste</h4>
                  {criticalForecastRows.length === 0 ? (
                    <p className="resource-note-muted">Acil tedarik gereken malzeme yok.</p>
                  ) : (
                    criticalForecastRows.map((row) => (
                      <button key={row.id} type="button" className="forecast-link" onClick={() => setSelectedResourceId(row.id)}>
                        {row.name}
                        <span>{formatNumber(row.recommended_order_quantity, 2)} {row.unit || ''}</span>
                      </button>
                    ))
                  )}
                </div>
                <div>
                  <h4>Sipariş Planı</h4>
                  {warningForecastRows.length === 0 ? (
                    <p className="resource-note-muted">Yakın sipariş planı gereken malzeme yok.</p>
                  ) : (
                    warningForecastRows.map((row) => (
                      <button key={row.id} type="button" className="forecast-link" onClick={() => setSelectedResourceId(row.id)}>
                        {row.name}
                        <span>{row.days_until_reorder === null ? 'Veri yok' : `${formatNumber(row.days_until_reorder, 1)} g?n`}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {selectedResource.type === 'material' && (
              <div className="resource-note-card">
                <div className="resource-card-head">
                  <strong>Gerçek Tüketim Kayıtları</strong>
                  {canManageResources && (
                    <button type="button" className="btn-icon" onClick={() => openCreateUsageLog(selectedResource)}>
                      Tüketim Ekle
                    </button>
                  )}
                </div>
                {selectedResourceUsageLogs.length === 0 ? (
                  <p className="resource-note-muted">Bu malzeme için henüz miktarlı tüketim kaydı girilmedi.</p>
                ) : (
                  <div className="purchase-order-list">
                    {selectedResourceUsageLogs.slice(0, 8).map((usage) => (
                      <div key={usage.id} className="purchase-order-item">
                        <div className="purchase-order-main">
                          <div className="purchase-order-title-row">
                            <strong>{usage.project_name || 'Genel stok tüketimi'}</strong>
                            <span className="team-badge warning">
                              {formatNumber(usage.quantity, 2)} {usage.unit || selectedResource.unit || ''}
                            </span>
                          </div>
                          <div className="resource-note-muted">
                            Tarih: {usage.usage_date ? new Date(usage.usage_date).toLocaleDateString('tr-TR') : '-'}
                            {' ? '}
                            Kaydeden: {usage.recorded_by_name || '-'}
                          </div>
                          <div className="resource-note-muted">{usage.notes || 'Not girilmedi.'}</div>
                        </div>
                        {canManageResources && (
                          <div className="team-item-actions">
                            <button type="button" className="btn-icon" onClick={() => openEditUsageLog(usage)}>Düzenle</button>
                            <button type="button" className="btn-icon danger" onClick={() => handleDeleteUsageLog(usage.id)}>Sil</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedResource.type === 'material' && (
              <div className="resource-note-card">
                <div className="resource-card-head">
                  <strong>Satın Alma ve Sipariş Takibi</strong>
                  {canManageResources && (
                    <button type="button" className="btn-icon" onClick={() => openCreatePurchaseOrder(selectedResource, selectedForecast)}>
                      Yeni Sipariş
                    </button>
                  )}
                </div>
                {selectedResourceOrders.length === 0 ? (
                  <p className="resource-note-muted">Bu malzeme için henüz sipariş talebi oluşturulmadı.</p>
                ) : (
                  <div className="purchase-order-list">
                    {selectedResourceOrders.slice(0, 8).map((order) => (
                      <div key={order.id} className="purchase-order-item">
                        <div className="purchase-order-main">
                          <div className="purchase-order-title-row">
                            <strong>{order.project_name || 'Genel Stok Talebi'}</strong>
                            <span className={`team-badge ${order.status === 'received' ? '' : order.status === 'cancelled' ? 'danger' : 'warning'}`}>
                              {PURCHASE_STATUS_OPTIONS.find((item) => item.value === order.status)?.label || order.status}
                            </span>
                          </div>
                          <div className="resource-note-muted">
                            {formatNumber(order.quantity, 2)} {order.unit || selectedResource.unit || ''} ? {order.supplier_name || 'Tedarikçi girilmedi'}
                          </div>
                          <div className="resource-note-muted">
                            Talep: {order.requested_date ? new Date(order.requested_date).toLocaleDateString('tr-TR') : '-'}
                            {' ? '}
                            Beklenen teslim: {order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString('tr-TR') : '-'}
                          </div>
                          <div className="resource-note-muted">
                            Fatura: {order.invoice_number || 'Girilmedi'}
                            {' | '}
                            Ödeme: {PAYMENT_STATUS_OPTIONS.find((item) => item.value === order.payment_status)?.label || 'Beklemede'}
                          </div>
                        </div>
                        <div className="team-item-actions">
                          {canManageDocuments && (
                            <button type="button" className="btn-icon" onClick={() => openPurchaseDocumentModal(order)}>Belge</button>
                          )}
                          {canManageResources && (
                            <>
                              <button type="button" className="btn-icon" onClick={() => openEditPurchaseOrder(order)}>Düzenle</button>
                              <button type="button" className="btn-icon danger" onClick={() => handleDeletePurchaseOrder(order.id)}>Sil</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showForm && renderForm(newRes, setNewRes, handleCreate, 'Yeni Kaynak Ekle', () => setShowForm(false))}
      {editingRes && renderForm(editingRes, setEditingRes, handleUpdate, 'Kaynak Düzenle', () => setEditingRes(null))}
      {showPurchaseForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingPurchaseOrder ? 'Sipariş Talebini Düzenle' : 'Yeni Sipariş Talebi'}</h3>
            <form onSubmit={handlePurchaseSubmit}>
              <div className="form-group">
                <label>Proje</label>
                <select className="form-control" value={purchaseOrderForm.project_id} onChange={(e) => handlePurchaseFieldChange('project_id', e.target.value)}>
                  <option value="">Genel stok / proje dışı</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div className="resource-form-grid">
                <div className="form-group">
                  <label>Miktar</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={purchaseOrderForm.quantity} onChange={(e) => handlePurchaseFieldChange('quantity', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Birim</label>
                  <input className="form-control" type="text" value={purchaseOrderForm.unit} onChange={(e) => handlePurchaseFieldChange('unit', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Birim Fiyat</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={purchaseOrderForm.unit_price} onChange={(e) => handlePurchaseFieldChange('unit_price', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Toplam Tutar</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={purchaseOrderForm.total_amount} onChange={(e) => handlePurchaseFieldChange('total_amount', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Durum</label>
                  <select className="form-control" value={purchaseOrderForm.status} onChange={(e) => handlePurchaseFieldChange('status', e.target.value)}>
                    {PURCHASE_STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tedarikçi</label>
                  <input className="form-control" type="text" value={purchaseOrderForm.supplier_name} onChange={(e) => handlePurchaseFieldChange('supplier_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Talep Tarihi</label>
                  <input className="form-control" type="date" value={purchaseOrderForm.requested_date} onChange={(e) => handlePurchaseFieldChange('requested_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Beklenen Teslim</label>
                  <input className="form-control" type="date" value={purchaseOrderForm.expected_delivery_date} onChange={(e) => handlePurchaseFieldChange('expected_delivery_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Teslim Tarihi</label>
                  <input className="form-control" type="date" value={purchaseOrderForm.delivered_date} onChange={(e) => handlePurchaseFieldChange('delivered_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Teslim Alınan Miktar</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={purchaseOrderForm.received_quantity} onChange={(e) => handlePurchaseFieldChange('received_quantity', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Fatura No</label>
                  <input className="form-control" type="text" value={purchaseOrderForm.invoice_number} onChange={(e) => handlePurchaseFieldChange('invoice_number', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Fatura Tarihi</label>
                  <input className="form-control" type="date" value={purchaseOrderForm.invoice_date} onChange={(e) => handlePurchaseFieldChange('invoice_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Fatura Tutari</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={purchaseOrderForm.invoice_amount} onChange={(e) => handlePurchaseFieldChange('invoice_amount', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Ödeme Durumu</label>
                  <select className="form-control" value={purchaseOrderForm.payment_status} onChange={(e) => handlePurchaseFieldChange('payment_status', e.target.value)}>
                    {PAYMENT_STATUS_OPTIONS.map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Not</label>
                <textarea className="form-control" rows="3" value={purchaseOrderForm.notes} onChange={(e) => handlePurchaseFieldChange('notes', e.target.value)} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">{editingPurchaseOrder ? 'Güncelle' : 'Kaydet'}</button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowPurchaseForm(false);
                    setEditingPurchaseOrder(null);
                    setPurchaseOrderForm(emptyPurchaseOrderState);
                  }}
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedOrderForDocs && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Sipariş Belgeleri</h3>
            <p className="resource-note-muted">
              {selectedOrderForDocs.project_name || 'Genel stok talebi'} - {selectedOrderForDocs.supplier_name || 'Tedarikçi girilmedi'}
            </p>
            {canManageDocuments && (
              <form onSubmit={handleUploadPurchaseDocument}>
              <div className="resource-form-grid">
                <div className="form-group">
                  <label>Belge Tipi</label>
                  <select className="form-control" value={purchaseDocumentForm.document_type} onChange={(e) => setPurchaseDocumentForm((prev) => ({ ...prev, document_type: e.target.value }))}>
                    {PURCHASE_DOCUMENT_TYPE_OPTIONS.map((item) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Dosya</label>
                  <input className="form-control" type="file" onChange={(e) => setPurchaseDocumentForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))} required />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-submit">Belge Yükle</button>
                <button type="button" className="btn-cancel" onClick={() => { setSelectedOrderForDocs(null); setOrderDocuments([]); setPurchaseDocumentForm(emptyPurchaseDocumentState); }}>
                  Kapat
                </button>
              </div>
              </form>
            )}

            <div className="purchase-document-list">
              {orderDocuments.length === 0 ? (
                <p className="resource-note-muted">Bu sipariş için belge yüklenmedi.</p>
              ) : (
                orderDocuments.map((doc) => (
                  <div key={doc.id} className="document-row">
                    <div>
                      <div className="document-title">{PURCHASE_DOCUMENT_TYPE_OPTIONS.find((item) => item.value === doc.document_type)?.label || 'Belge'}</div>
                      <div className="resource-note-muted">{doc.original_name || doc.file_path?.split('/').pop() || 'Dosya'}</div>
                      <div className="resource-note-muted">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('tr-TR') : '-'}</div>
                    </div>
                    <div className="team-item-actions">
                      <button type="button" className="btn-icon" onClick={() => handleDownloadPurchaseDocument(doc)}>Indir</button>
                      {canManageDocuments && (
                        <button type="button" className="btn-icon danger" onClick={() => handleDeletePurchaseDocument(doc.id)}>Sil</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {showUsageForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>{editingUsageLog ? 'Tüketim Kaydını Düzenle' : 'Yeni Tüketim Kaydı'}</h3>
            <form onSubmit={handleUsageSubmit}>
              <div className="form-group">
                <label>Proje</label>
                <select className="form-control" value={usageLogForm.project_id} onChange={(e) => handleUsageFieldChange('project_id', e.target.value)}>
                  <option value="">Genel stok tüketimi</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div className="resource-form-grid">
                <div className="form-group">
                  <label>Tüketim Tarihi</label>
                  <input className="form-control" type="date" value={usageLogForm.usage_date} onChange={(e) => handleUsageFieldChange('usage_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Miktar</label>
                  <input className="form-control" type="number" min="0" step="0.01" value={usageLogForm.quantity} onChange={(e) => handleUsageFieldChange('quantity', e.target.value)} required />
                </div>
                <div className="form-group">
                  <label>Birim</label>
                  <input className="form-control" type="text" value={usageLogForm.unit} onChange={(e) => handleUsageFieldChange('unit', e.target.value)} />
                </div>
              </div>

              <div className="form-group">
                <label>Not</label>
                <textarea className="form-control" rows="3" value={usageLogForm.notes} onChange={(e) => handleUsageFieldChange('notes', e.target.value)} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">{editingUsageLog ? 'Güncelle' : 'Kaydet'}</button>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowUsageForm(false);
                    setEditingUsageLog(null);
                    setUsageLogForm(emptyUsageLogState);
                  }}
                >
                  ?ptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources;





